import type { TileDefinition } from "../contract/definition";
import type { ConfiguratorCstic, ConfiguratorInstanceContext } from "../contract/configuration";
import type { ResolvedControlModel, ResolvedStepModel } from "./deriveConfiguratorModel";

export interface ConfigurationSelectionEntry {
  controlIndex: number;
  csticName: string;
  labelKey: string;
  summaryLabel: string;
  value: string;
  valueLabelKey?: string;
  unit?: string;
  standard?: boolean;
  miniSummary: boolean;
}

export interface ConfigurationSelectionInstance {
  instanceId: string;
  kmatType: string;
  kmatIndex: number;
  logicalIndex: number;
  instanceCount: number;
  entries: readonly ConfigurationSelectionEntry[];
}

export interface ConfigurationSelectionStep {
  stepIndex: number;
  titleKey: string;
  instances: readonly ConfigurationSelectionInstance[];
}

export interface ConfigurationSelectionModel {
  steps: readonly ConfigurationSelectionStep[];
}

const referencesMatch = (
  references: readonly { cstic: string; value: string | null }[],
  context: ConfiguratorInstanceContext,
) => references.every((reference) => context.instance.cstics[reference.cstic]?.value === reference.value);

const findSelectedTile = (control: ResolvedControlModel): TileDefinition | undefined => {
  if (control.definition.type !== "TileSelect") {
    return undefined;
  }

  return control.definition.tiles?.find((tile) => referencesMatch(tile.selectedWhen ?? tile.writes, control.context));
};

const buildEntry = (control: ResolvedControlModel): ConfigurationSelectionEntry | null => {
  const summary = control.definition.summary;
  const cstic: ConfiguratorCstic | undefined = control.context.instance.cstics[control.definition.cstic];

  if (!control.visible || summary?.include === false || !cstic?.value) {
    return null;
  }

  const selectedTile = findSelectedTile(control);
  const selectedValue = cstic.values.find((value) => value.value === cstic.value);

  return {
    controlIndex: control.controlIndex,
    csticName: control.definition.cstic,
    labelKey: summary?.labelKey ?? control.definition.labelKey ?? cstic.labelKey,
    summaryLabel: cstic.summaryLabel,
    value: cstic.value,
    valueLabelKey: selectedTile?.labelKey ?? selectedValue?.labelKey,
    unit: cstic.unit,
    standard: selectedValue?.standard,
    miniSummary: summary?.miniSummary ?? true,
  };
};

export const buildConfigurationSelectionModel = (steps: readonly ResolvedStepModel[]): ConfigurationSelectionModel => ({
  steps: steps.map((step) => {
    const instances = new Map<string, ConfigurationSelectionInstance>();

    if (!step.visible || step.status === "disabled") {
      return {
        stepIndex: step.stepIndex,
        titleKey: step.definition.titleKey,
        instances: [],
      };
    }

    step.substeps
      .filter((substep) => substep.visible)
      .forEach((substep) => {
        substep.controls.forEach((control) => {
          const entry = buildEntry(control);
          if (!entry) {
            return;
          }

          const { instance, logicalIndex, instanceCount } = control.context;
          const selectionInstance = instances.get(instance.id) ?? {
            instanceId: instance.id,
            kmatType: instance.kmatType,
            kmatIndex: instance.kmatIndex,
            logicalIndex,
            instanceCount,
            entries: [],
          };

          instances.set(instance.id, { ...selectionInstance, entries: [...selectionInstance.entries, entry] });
        });
      });

    return {
      stepIndex: step.stepIndex,
      titleKey: step.definition.titleKey,
      instances: Array.from(instances.values()),
    };
  }),
});
